<template>

    <div class="px-6 py-4 bg-minplan-beige -mb-4">
        <page-title :title="$t('apartments.editApartment')"></page-title>
    </div>

    <jet-form-section @submitted="updateApartment()">

        <template #form>

            <!-- Card Apartment Information -->
            <form-card :title="$t('apartments.apartment')" :description="$t('apartments.apartmentDescription')" class="shadow-none">
                <!-- Durchgehendes Raster wie im Anlege-Modal: zwei gleich breite
                     Felder je Zeile. -->
                <div class="col-span-3">
                    <label for="number" class="block text-sm font-medium text-gray-700">{{
                            $t("apartments.number")
                        }}</label>
                    <input type="text" name="number" id="number" v-model="form.updateBase.number"
                           class="mt-1 focus:ring-minplan-blue focus:border-minplan-blue block w-full shadow-sm sm:text-sm border-gray-300"
                           autofocus />
                    <jet-input-error :message="form.errors['updateBase.number']" class="mt-2" />
                </div>


                <div class="col-span-3">
                    <label for="floor" class="block text-sm font-medium text-gray-700">{{ $t('apartments.floor') }} <RequiredMark /></label>
                    <!-- Wie im Anlege-Modal: das Stockwerkfeld trug dieselbe id wie die
                         Nettowohnfläche, das Label zeigte aufs falsche Feld. -->
                    <input type="text" name="floor" id="floor" v-model="form.updateBase.floor" class="mt-1 focus:ring-minplan-blue focus:border-minplan-blue block w-full shadow-sm sm:text-sm border-gray-300" />
                    <jet-input-error :message="form.errors['updateBase.floor']" class="mt-2" />
                </div>

                <div class="col-span-3">
                    <label for="rooms" class="block text-sm font-medium text-gray-700">{{
                            $t("apartments.roomsAmount")
                        }} <RequiredMark /></label>
                    <Dropdown v-model="selectedRoom"
                              id="rooms"
                              :options="rooms"
                              optionLabel="name"
                              :filter="true"
                              :auto-filter-focus="true"
                              filter-match-mode="startsWith"
                              :placeholder="$t('apartments.roomsAmount')"
                              :showClear="false"
                              class="w-full mt-1 min-w-0 sm:text-sm">
                    </Dropdown>
                    <jet-input-error :message="form.errors['updateBase.rooms']" class="mt-2" />
                </div>

                <div class="col-span-3">
                    <label for="area" class="block text-sm font-medium text-gray-700">{{
                            $t("apartments.area")
                        }} <RequiredMark /></label>
                    <div class="flex">
                        <input type="number" step=".01" name="area" id="area" v-model="form.updateBase.area"
                               class="mt-1 focus:ring-minplan-blue focus:border-minplan-blue block w-full shadow-sm sm:text-sm border-gray-300" />
                        <span
                            class="inline-flex items-center px-3 border border-l-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm mt-1">&#13217;</span>
                    </div>
                    <jet-input-error :message="form.errors['updateBase.area']" class="mt-2" />
                </div>

                <!-- Parkplatz in eigener Zeile: hier gibt es kein Gebäude-Feld
                     (die Zuordnung läuft über das Ziehen in der Liste), neben
                     dem er im Anlege-Modal steht. -->
                <div class="col-span-3">
                    <label for="parking" class="block text-sm font-medium text-gray-700">{{ $t('apartments.parking') }}</label>
                    <input type="text" name="parking" id="parking" v-model="form.updateBase.parking" :placeholder="$t('apartments.parkingPlaceholder')" class="mt-1 focus:ring-minplan-blue focus:border-minplan-blue block w-full shadow-sm sm:text-sm border-gray-300" />
                    <jet-input-error :message="form.errors['updateBase.parking']" class="mt-2" />
                </div>

            </form-card>

            <!-- Card Apartment Settings -->
            <form-card :title="$t('apartments.settings')" :description="$t('apartments.settingsDescription')">
                <div class="col-span-3">
                    <label for="base_fee" class="block text-sm font-medium text-gray-700">{{ $t("projects.baseFee") }} <RequiredMark /></label>
                    <div class="flex">
                        <input type="number" step="0.01" name="base_fee" id="base_fee" v-model="form.updateBase.base_fee" class="mt-1 focus:ring-minplan-blue focus:border-minplan-blue block w-full shadow-sm sm:text-sm border-gray-300" />
                        <span class="inline-flex items-center px-3 border border-l-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm mt-1">%</span>
                    </div>
                    <jet-input-error :message="form.errors['updateBase.base_fee']" class="mt-2" />
                </div>

                <div class="col-span-3">
                    <label for="consultancy_fee" class="block text-sm font-medium text-gray-700">{{ $t("projects.consultancyFee") }} <RequiredMark /></label>
                    <div class="flex">
                        <input type="number" step="0.01" name="consultancy_fee" id="consultancy_fee" v-model="form.updateBase.consultancy_fee" class="mt-1 focus:ring-minplan-blue focus:border-minplan-blue block w-full shadow-sm sm:text-sm border-gray-300" />
                        <span class="inline-flex items-center px-3 border border-l-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm mt-1">%</span>
                    </div>
                    <jet-input-error :message="form.errors['updateBase.consultancy_fee']" class="mt-2" />
                </div>
            </form-card>

        </template>

        <template #actions>
            <div class="flex justify-end my-4 mx-6">

                <button
                    type="button"
                    @click="cancel" class="inline-block uppercase font-semibold text-xs tracking-widest">
                    {{ $t("actions.cancel") }}
                </button>

                <primary-button :class="{ 'opacity-25': form.processing }" :disabled="form.processing" class="ml-3 ">
                    {{ $t("actions.save") }}
                </primary-button>

            </div>
        </template>

    </jet-form-section>

</template>


<script>
import { defineComponent, ref } from "vue";
import { rooms } from "@/Shared/rooms";
import MainLayout from "@/Layouts/MainLayout";
import PrimaryButton from "@/Jetstream/PrimaryButton";
import JetSecondaryButton from "@/Jetstream/SecondaryButton";
import JetFormSection from "@/Jetstream/FormSection";
import JetInput from "@/Jetstream/Input";
import JetInputError from "@/Jetstream/InputError";
import JetLabel from "@/Jetstream/Label";
import PageTitle from "@/Shared/Components/PageTitle";
import RequiredMark from "@/Shared/Components/RequiredMark";
import { useForm } from "@inertiajs/inertia-vue3";
import FormCard from "@/Shared/Components/FormCard";

export default defineComponent({
    props: ["apartment"],
    inheritAttrs: false,
    layout: MainLayout,
    emits: ["closeModal"],
    components: {
        PageTitle,
        PrimaryButton,
        JetFormSection,
        JetInput,
        JetInputError,
        JetLabel,
        JetSecondaryButton,
        FormCard,
        RequiredMark
    },
    methods: {
        cancel() {
            this.$emit("closeModal");
        },
        updateApartment() {
            this.form.updateBase.rooms = this.selectedRoom ? this.selectedRoom.value : null;
            this.form.put(this.route("advisors.apartments.update", this.apartment.uuid), {
                preserveScroll: true,
                onSuccess: () => this.$notify({
                    group: "success",
                    title: "actions.success",
                    text: "apartments.updated"
                })
            });
        }
    },
    setup(props) {
        const selectedRoom = ref(props.apartment.rooms ? rooms.find(r => r.value === props.apartment.rooms) : null);

        const form = useForm({
            updateBase: {
                number: props.apartment.number,
                floor: props.apartment.floor,
                parking: props.apartment.parking,
                rooms: props.apartment.rooms,
                area: props.apartment.area,
                base_fee: props.apartment.base_fee,
                consultancy_fee: props.apartment.consultancy_fee,
            }
        });

        return { rooms, form, selectedRoom };
    }
});
</script>

<style scoped></style>
